import { useState, useEffect, useCallback } from 'preact/hooks';
import { get } from '../api';
import type { ContentSeed, ContentBrief, ContentDraft, PipelineStats } from '../types';
import { SearchBox } from '../components/database/SearchBox';
import { TableSelector } from '../components/database/TableSelector';
import type { TableType } from '../components/database/TableSelector';
import { FilterPanel } from '../components/database/FilterPanel';
import { DataTable } from '../components/database/DataTable';
import { Pagination } from '../components/database/Pagination';
import { RecordDetail } from '../components/database/RecordDetail';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';

export function Database() {
  const [activeTable, setActiveTable] = useState<TableType>('seeds');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortField, setSortField] = useState('timestamp');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [items, setItems] = useState<(ContentSeed | ContentBrief | ContentDraft)[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ seeds: 0, briefs: 0, drafts: 0 });

  // Load pipeline stats for table counts
  useEffect(() => {
    get<PipelineStats>('/api/stats').then((stats) => {
      setCounts({
        seeds: stats.seeds_total,
        briefs: stats.briefs_total,
        drafts: stats.drafts_total,
      });
    }).catch(console.error);
  }, []);

  // Fetch data when table, query, page, limit, or filters change
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = statusFilters.length > 0 ? `&status=${statusFilters[0]}` : '';
      const queryParam = appliedQuery ? `&q=${encodeURIComponent(appliedQuery)}` : '';

      if (activeTable === 'seeds') {
        const data = await get<{ items: ContentSeed[]; total: number; page: number; limit: number; pages: number }>(
          `/api/seeds?page=${page}&limit=${limit}${statusParam}${queryParam}`
        );
        setItems(data.items);
        setTotal(data.total);
        setPages(data.pages);
      } else if (activeTable === 'briefs') {
        const data = await get<{ items: ContentBrief[]; total: number; page: number; limit: number; pages: number }>(
          `/api/briefs?page=${page}&limit=${limit}${statusParam}${queryParam}`
        );
        setItems(data.items);
        setTotal(data.total);
        setPages(data.pages);
      } else {
        const data = await get<{ drafts: ContentDraft[] }>(`/api/drafts?status=${statusFilters[0] || ''}`);
        const drafts = data.drafts || [];
        // Client-side search for drafts (API doesn't support q param)
        const filtered = appliedQuery
          ? drafts.filter((d) => d.headline.toLowerCase().includes(appliedQuery.toLowerCase()))
          : drafts;
        setTotal(filtered.length);
        setPages(Math.max(1, Math.ceil(filtered.length / limit)));
        setItems(filtered.slice((page - 1) * limit, page * limit));
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setItems([]);
      setTotal(0);
      setPages(1);
    }
    setLoading(false);
  }, [activeTable, appliedQuery, page, limit, statusFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTableChange = (table: TableType) => {
    setActiveTable(table);
    setPage(1);
    setSelectedId(null);
    setStatusFilters([]);
    setSortField(table === 'seeds' ? 'timestamp' : 'created_at');
  };

  const handleSearch = () => {
    setAppliedQuery(query);
    setPage(1);
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchData();
  };

  const handleClearFilters = () => {
    setStatusFilters([]);
    setQuery('');
    setAppliedQuery('');
    setPage(1);
  };

  return (
    <div class="grid-database">
      {/* LEFT: Table Selector + Filters */}
      <div class="db-left">
        <div class="lcars-panel" style={{ marginBottom: 0 }}>
          <div class="panel-chrome">
            <div class="panel-pill" style={{ background: 'var(--orange)' }}></div>
            <div class="panel-bar" style={{ background: 'var(--orange)' }}>
              <span class="panel-title">Content Database</span>
            </div>
          </div>
        </div>

        <div class="db-left-header">
          <SearchBox value={query} onChange={setQuery} onSubmit={handleSearch} />
        </div>

        <TableSelector activeTable={activeTable} onSelect={handleTableChange} counts={counts} />

        <FilterPanel
          activeTable={activeTable}
          statusFilters={statusFilters}
          onStatusChange={setStatusFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </div>

      {/* CENTRE: Results Table + Pagination */}
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <DataTable
            activeTable={activeTable}
            items={items}
            total={total}
            query={appliedQuery}
            selectedId={selectedId}
            onSelect={setSelectedId}
            sortField={sortField}
            onSortChange={setSortField}
          />
        )}
        <Pagination
          page={page}
          pages={pages}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
        />
      </div>

      {/* RIGHT: Detail Panel */}
      {selectedId ? (
        <RecordDetail activeTable={activeTable} recordId={selectedId} onClose={() => setSelectedId(null)} />
      ) : (
        <div class="db-right">
          <div class="empty-state">
            <div class="empty-state-icon">◇</div>
            <div class="empty-state-text">Select a record to view details</div>
          </div>
        </div>
      )}
    </div>
  );
}
